
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CalendarDateRangePicker } from "@/components/calendar-date-range-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { DateRange } from "react-day-picker";
import { 
  DollarSign, 
  CreditCard, 
  Calendar as CalendarIcon, 
  RefreshCw, 
  Filter, 
  Plus, 
  Download, 
  Printer, 
  Settings, 
  ArrowDownRight, 
  ArrowUpRight, 
  ChevronDown, 
  FileCog, 
  Search,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  LineChart as LineChartIcon,
  FileText,
  FileSpreadsheet,
  X as XIcon,
  PieChart as PieChartIcon,
  Clock,
  Info
} from "lucide-react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  Scatter,
  ScatterChart,
  ZAxis,
  LineChart,
  Line
} from 'recharts';

// Type definitions
interface DateRangeType {
  from: Date;
  to: Date;
}

interface Transaction {
  id: number;
  type: string;
  amount: number;
  date: string;
  status: string;
  category: string;
  paymentMethod: string;
  project?: string;
  client?: string;
  description?: string;
}

interface TransactionFilterState {
  type: string[];
  category: string[];
  status: string[];
  dateRange: { from: Date | null; to: Date | null };
  minAmount: number;
  maxAmount: number;
  search: string;
}

// Sample data for charts
const incomeData = [
  { month: 'Jan', amount: 7500, pending: 2000, paid: 5500 },
  { month: 'Feb', amount: 8200, pending: 1800, paid: 6400 },
  { month: 'Mar', amount: 9800, pending: 2500, paid: 7300 },
  { month: 'Apr', amount: 8900, pending: 1900, paid: 7000 },
  { month: 'May', amount: 10200, pending: 2200, paid: 8000 },
  { month: 'Jun', amount: 11500, pending: 2500, paid: 9000 },
  { month: 'Jul', amount: 10800, pending: 2100, paid: 8700 },
];

const categoryData = [
  { name: 'Development', value: 14500, fill: '#3b82f6' },
  { name: 'Design', value: 9200, fill: '#8b5cf6' },
  { name: 'Consulting', value: 7800, fill: '#f59e0b' },
  { name: 'Content', value: 5500, fill: '#10b981' },
  { name: 'Other', value: 3000, fill: '#6b7280' },
];

const clientData = [
  { name: 'TechCorp', value: 12500, fill: '#3b82f6' },
  { name: 'DesignHub', value: 8800, fill: '#8b5cf6' },
  { name: 'StartupX', value: 6500, fill: '#f59e0b' },
  { name: 'GlobalInc', value: 7200, fill: '#10b981' },
  { name: 'OtherClients', value: 5000, fill: '#6b7280' },
];

const paymentTrendData = [
  { month: 'Jan', ontime: 95, late: 5 },
  { month: 'Feb', ontime: 92, late: 8 },
  { month: 'Mar', ontime: 97, late: 3 },
  { month: 'Apr', ontime: 90, late: 10 },
  { month: 'May', ontime: 94, late: 6 },
  { month: 'Jun', ontime: 96, late: 4 },
  { month: 'Jul', ontime: 98, late: 2 },
];

const transactions: Transaction[] = [
  { id: 1, type: 'invoice', project: 'Website Redesign', client: 'TechCorp', amount: 2500, date: '2023-07-15', status: 'paid', category: 'Development', paymentMethod: 'Bank Transfer' },
  { id: 2, type: 'invoice', project: 'Mobile App UI', client: 'DesignHub', amount: 1800, date: '2023-07-10', status: 'paid', category: 'Design', paymentMethod: 'Credit Card' },
  { id: 3, type: 'invoice', project: 'SEO Optimization', client: 'StartupX', amount: 1200, date: '2023-07-05', status: 'pending', category: 'Consulting', paymentMethod: 'Pending' },
  { id: 4, type: 'invoice', project: 'E-commerce Platform', client: 'GlobalInc', amount: 3500, date: '2023-06-28', status: 'paid', category: 'Development', paymentMethod: 'Bank Transfer' },
  { id: 5, type: 'invoice', project: 'Social Media Campaign', client: 'MarketingPro', amount: 1500, date: '2023-06-20', status: 'pending', category: 'Marketing', paymentMethod: 'Pending' },
  { id: 6, type: 'expense', category: 'Software', description: 'Adobe Creative Cloud', amount: 52.99, date: '2023-07-01', status: 'paid', paymentMethod: 'Credit Card' },
  { id: 7, type: 'expense', category: 'Hardware', description: 'External SSD', amount: 129.99, date: '2023-06-15', status: 'paid', paymentMethod: 'Credit Card' },
  { id: 8, type: 'expense', category: 'Office', description: 'Co-working Space', amount: 350, date: '2023-07-01', status: 'paid', paymentMethod: 'Bank Transfer' },
  { id: 9, type: 'expense', category: 'Subscription', description: 'Figma Pro', amount: 45, date: '2023-06-28', status: 'paid', paymentMethod: 'Credit Card' },
  { id: 10, type: 'expense', category: 'Marketing', description: 'Facebook Ads', amount: 200, date: '2023-06-25', status: 'paid', paymentMethod: 'Credit Card' },
];

// Enhanced chart components using Recharts
const IncomeChart = ({ data, timeframe }: { data: any[]; timeframe: string }) => {
  const chartData = timeframe === 'month' ? data.slice(-3) : data;
  
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis dataKey="month" />
          <YAxis />
          <CartesianGrid strokeDasharray="3 3" />
          <RechartsTooltip />
          <Legend />
          <Area type="monotone" dataKey="amount" stroke="#3b82f6" fillOpacity={1} fill="url(#colorAmount)" />
          <Area type="monotone" dataKey="pending" stroke="#f59e0b" fillOpacity={1} fill="url(#colorPending)" />
          <Area type="monotone" dataKey="paid" stroke="#10b981" fillOpacity={1} fill="url(#colorPaid)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const CategoryPieChart = ({ data }: { data: any[] }) => {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            fill="#8884d8"
            paddingAngle={2}
            dataKey="value"
            label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Legend />
          <RechartsTooltip formatter={(value) => [`$${value}`, 'Amount']} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const ClientPieChart = ({ data }: { data: any[] }) => {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            fill="#8884d8"
            paddingAngle={2}
            dataKey="value"
            label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Legend />
          <RechartsTooltip formatter={(value) => [`$${value}`, 'Amount']} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const PaymentTrendChart = ({ data }: { data: any[] }) => {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <RechartsTooltip formatter={(value) => [`${value}%`, 'Percentage']} />
          <Legend />
          <Bar dataKey="ontime" fill="#10b981" name="On-time Payment" />
          <Bar dataKey="late" fill="#ef4444" name="Late Payment" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main component
const Income = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [timeframe, setTimeframe] = useState("month");
  const [chartType, setChartType] = useState("income");
  const [dateRange, setDateRange] = useState<DateRangeType>({ 
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)), 
    to: new Date() 
  });
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  
  const [newTransaction, setNewTransaction] = useState<Partial<Transaction>>({
    type: 'invoice',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    status: 'pending',
    category: 'Development',
    paymentMethod: 'Bank Transfer'
  });
  
  const [filters, setFilters] = useState<TransactionFilterState>({
    type: [],
    category: [],
    status: [],
    dateRange: { from: null, to: null },
    minAmount: 0,
    maxAmount: 10000,
    search: ''
  });
  
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>(transactions);
  const [exportFormat, setExportFormat] = useState('pdf');
  const { toast } = useToast();

  // Filter transactions based on current filters
  useEffect(() => {
    let results = [...transactions];
    
    // Filter by type
    if (filters.type.length > 0) {
      results = results.filter(t => filters.type.includes(t.type));
    }
    
    // Filter by category
    if (filters.category.length > 0) {
      results = results.filter(t => filters.category.includes(t.category));
    }
    
    // Filter by status
    if (filters.status.length > 0) {
      results = results.filter(t => filters.status.includes(t.status));
    }
    
    // Filter by date range
    if (filters.dateRange.from && filters.dateRange.to) {
      const fromDate = new Date(filters.dateRange.from);
      const toDate = new Date(filters.dateRange.to);
      
      results = results.filter(t => {
        const tDate = new Date(t.date);
        return tDate >= fromDate && tDate <= toDate;
      });
    }
    
    // Filter by amount range
    results = results.filter(t => 
      t.amount >= filters.minAmount && t.amount <= filters.maxAmount
    );
    
    // Filter by search term (across multiple fields)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      results = results.filter(t => 
        (t.category && t.category.toLowerCase().includes(searchTerm)) ||
        (t.project && t.project.toLowerCase().includes(searchTerm)) ||
        (t.client && t.client.toLowerCase().includes(searchTerm)) ||
        (t.description && t.description.toLowerCase().includes(searchTerm)) ||
        t.paymentMethod.toLowerCase().includes(searchTerm) ||
        t.status.toLowerCase().includes(searchTerm)
      );
    }
    
    setFilteredTransactions(results);
  }, [filters]);

  const handleAddTransaction = () => {
    const newId = transactions.length + 1;
    const transactionToAdd: Transaction = {
      id: newId,
      type: newTransaction.type || 'invoice',
      amount: newTransaction.amount || 0,
      date: newTransaction.date || new Date().toISOString().split('T')[0],
      status: newTransaction.status || 'pending',
      category: newTransaction.category || 'Development',
      paymentMethod: newTransaction.paymentMethod || 'Bank Transfer',
      ...(newTransaction.type === 'invoice' 
         ? { project: newTransaction.project, client: newTransaction.client } 
         : { description: newTransaction.description })
    } as Transaction;
    
    // In a real app, you would add this to your database
    // For this demo, we'll just update the local state and close the modal
    
    toast({
      title: "Transaction Added",
      description: `${transactionToAdd.type === 'invoice' ? 'Invoice' : 'Expense'} of $${transactionToAdd.amount} has been added successfully.`,
      variant: "success",
    });
    
    setIsAddModalOpen(false);
    
    // Reset the form
    setNewTransaction({
      type: 'invoice',
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      status: 'pending',
      category: 'Development',
      paymentMethod: 'Bank Transfer'
    });
  };

  const handleExport = () => {
    toast({
      title: "Exporting Data",
      description: `Your income data is being exported as ${exportFormat.toUpperCase()}.`,
    });
    setTimeout(() => {
      setIsExportModalOpen(false);
      toast({
        title: "Export Complete",
        description: "Your income data has been exported successfully.",
        variant: "success",
      });
    }, 1500);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle DateRange type issue
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from) {
      setDateRange({
        from: range.from,
        to: range.to || range.from
      });
      setTimeframe("custom");
    }
  };

  // Calculate totals
  const totalIncome = transactions
    .filter(t => t.type === 'invoice')
    .reduce((sum, t) => sum + t.amount, 0);
    
  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
    
  const netIncome = totalIncome - totalExpenses;
  
  const pendingIncome = transactions
    .filter(t => t.type === 'invoice' && t.status === 'pending')
    .reduce((sum, t) => sum + t.amount, 0);
  
  return (
    <div className="container max-w-7xl mx-auto p-4 md:p-8 animate-in">
      <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-green-600">
            Income & Expenses
          </h1>
          <p className="text-muted-foreground">
            Track your income, expenses and financial performance
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                {timeframe === 'week' ? 'This Week' : 
                 timeframe === 'month' ? 'This Month' : 
                 timeframe === 'quarter' ? 'This Quarter' : 
                 timeframe === 'year' ? 'This Year' : 'Custom Range'}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-4 flex flex-col gap-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    size="sm" 
                    variant={timeframe === 'week' ? 'default' : 'outline'}
                    onClick={() => setTimeframe('week')}
                  >
                    This Week
                  </Button>
                  <Button 
                    size="sm" 
                    variant={timeframe === 'month' ? 'default' : 'outline'}
                    onClick={() => setTimeframe('month')}
                  >
                    This Month
                  </Button>
                  <Button 
                    size="sm" 
                    variant={timeframe === 'quarter' ? 'default' : 'outline'}
                    onClick={() => setTimeframe('quarter')}
                  >
                    This Quarter
                  </Button>
                  <Button 
                    size="sm" 
                    variant={timeframe === 'year' ? 'default' : 'outline'}
                    onClick={() => setTimeframe('year')}
                  >
                    This Year
                  </Button>
                </div>
                
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm font-medium mb-2">Custom Date Range</p>
                  <CalendarDateRangePicker 
                    dateRange={dateRange}
                    setDateRange={handleDateRangeChange}
                    className="w-full"
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => setIsFilterModalOpen(true)}
          >
            <Filter className="h-4 w-4" />
            Filters
            {Object.values(filters).some(val => 
              Array.isArray(val) ? val.length > 0 : 
              typeof val === 'object' ? (val.from || val.to) : 
              val !== 0 && val !== 10000 && Boolean(val)
            ) && (
              <Badge variant="secondary" className="ml-1 h-5 px-1">!</Badge>
            )}
          </Button>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => {
              toast({
                title: "Refreshing Data",
                description: "Your financial data is being updated...",
              });
              setTimeout(() => {
                toast({
                  title: "Data Refreshed",
                  description: "Your financial data has been updated with the latest transactions.",
                  variant: "success",
                });
              }, 1000);
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          
          <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Transaction
          </Button>
          
          <div className="hidden sm:flex items-center gap-2">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => setIsExportModalOpen(true)}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
            
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4" />
              Print
            </Button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Total Income
            </CardTitle>
            <DollarSign className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalIncome.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-green-600 inline-flex items-center">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +8.2%
              </span>{" "}
              vs previous period
            </p>
            <Progress className="h-2 mt-3" value={85} />
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Total Expenses
            </CardTitle>
            <CreditCard className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalExpenses.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-red-600 inline-flex items-center">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +2.5%
              </span>{" "}
              vs previous period
            </p>
            <Progress className="h-2 mt-3" value={32} indicatorClassName="bg-red-500" />
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Net Income
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${netIncome.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-green-600 inline-flex items-center">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +10.4%
              </span>{" "}
              vs previous period
            </p>
            <Progress className="h-2 mt-3" value={78} indicatorClassName="bg-emerald-500" />
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Income
            </CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${pendingIncome.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-amber-600 inline-flex items-center">
                <ArrowDownRight className="h-3 w-3 mr-1" />
                -3.1%
              </span>{" "}
              vs previous period
            </p>
            <Progress className="h-2 mt-3" value={45} indicatorClassName="bg-amber-500" />
          </CardContent>
        </Card>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full max-w-md mb-6 bg-muted">
          <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
          <TabsTrigger value="transactions" className="flex-1">Transactions</TabsTrigger>
          <TabsTrigger value="analytics" className="flex-1">Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Income Overview</CardTitle>
              <CardDescription>Your income breakdown over time</CardDescription>
            </CardHeader>
            <CardContent>
              <IncomeChart data={incomeData} timeframe={timeframe} />
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Income by Category</CardTitle>
                <CardDescription>Distribution across service categories</CardDescription>
              </CardHeader>
              <CardContent>
                <CategoryPieChart data={categoryData} />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Income by Client</CardTitle>
                <CardDescription>Distribution across client sources</CardDescription>
              </CardHeader>
              <CardContent>
                <ClientPieChart data={clientData} />
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Payment Trends</CardTitle>
              <CardDescription>On-time vs. late payment percentages</CardDescription>
            </CardHeader>
            <CardContent>
              <PaymentTrendChart data={paymentTrendData} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="transactions" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Transaction List</h2>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search transactions..." 
                className="pl-8"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
          
          <div className="rounded-md border">
            <div className="relative w-full overflow-auto">
              <table className="w-full caption-bottom text-sm">
                <thead className="[&_tr]:border-b">
                  <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Type</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Amount</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Date</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Category</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground hidden md:table-cell">
                      Description
                    </th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Payment Method</th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                        <td className="p-4 align-middle">
                          <Badge variant={transaction.type === 'invoice' ? 'default' : 'outline'} className="capitalize">
                            {transaction.type}
                          </Badge>
                        </td>
                        <td className="p-4 align-middle font-medium">
                          ${transaction.amount.toLocaleString()}
                        </td>
                        <td className="p-4 align-middle text-muted-foreground">
                          {new Date(transaction.date).toLocaleDateString()}
                        </td>
                        <td className="p-4 align-middle">
                          <Badge variant={
                            transaction.status === 'paid' ? 'success' : 
                            transaction.status === 'pending' ? 'warning' : 'outline'
                          }>
                            {transaction.status}
                          </Badge>
                        </td>
                        <td className="p-4 align-middle">
                          {transaction.category}
                        </td>
                        <td className="p-4 align-middle hidden md:table-cell">
                          {transaction.type === 'invoice' 
                            ? `${transaction.project} (${transaction.client})` 
                            : transaction.description}
                        </td>
                        <td className="p-4 align-middle">
                          {transaction.paymentMethod}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-muted-foreground">
                        No transactions found matching your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="analytics" className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Income Forecast</CardTitle>
              <CardDescription>Projected income based on current trends</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={[
                      { month: 'Aug', actual: 12500, forecasted: 0 },
                      { month: 'Sep', actual: 13200, forecasted: 0 },
                      { month: 'Oct', actual: 14500, forecasted: 0 },
                      { month: 'Nov', actual: 0, forecasted: 15700 },
                      { month: 'Dec', actual: 0, forecasted: 16900 },
                      { month: 'Jan', actual: 0, forecasted: 18200 },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => [`$${value}`, 'Amount']} />
                    <Legend />
                    <Line type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} name="Actual" />
                    <Line type="monotone" dataKey="forecasted" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} name="Forecasted" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Expense Breakdown</CardTitle>
                <CardDescription>Monthly expense distribution by category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { category: 'Software', amount: 350 },
                        { category: 'Hardware', amount: 480 },
                        { category: 'Office', amount: 780 },
                        { category: 'Marketing', amount: 1200 },
                        { category: 'Services', amount: 650 }
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="category" type="category" width={80} />
                      <RechartsTooltip formatter={(value) => [`$${value}`, 'Amount']} />
                      <Bar dataKey="amount" fill="#ef4444" barSize={30} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Income vs. Expenses</CardTitle>
                <CardDescription>Monthly comparison over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { month: 'Jul', income: 10800, expenses: 4200 },
                        { month: 'Aug', income: 12500, expenses: 4800 },
                        { month: 'Sep', income: 13200, expenses: 5100 },
                        { month: 'Oct', income: 14500, expenses: 5500 },
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => [`$${value}`, 'Amount']} />
                      <Legend />
                      <Bar dataKey="income" fill="#3b82f6" name="Income" />
                      <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Add Transaction Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
            <DialogDescription>
              Create a new income or expense record
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="invoice"
                  name="transactionType"
                  className="rounded border-gray-300 text-blue-600"
                  checked={newTransaction.type === 'invoice'}
                  onChange={() => setNewTransaction(prev => ({ ...prev, type: 'invoice' }))}
                />
                <Label htmlFor="invoice">Invoice (Income)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="expense"
                  name="transactionType"
                  className="rounded border-gray-300 text-blue-600"
                  checked={newTransaction.type === 'expense'}
                  onChange={() => setNewTransaction(prev => ({ ...prev, type: 'expense' }))}
                />
                <Label htmlFor="expense">Expense</Label>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={newTransaction.amount}
                  onChange={e => setNewTransaction(prev => ({ ...prev, amount: parseFloat(e.target.value) }))}
                />
              </div>
              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={newTransaction.date}
                  onChange={e => setNewTransaction(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={newTransaction.category}
                  onValueChange={value => setNewTransaction(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {newTransaction.type === 'invoice' ? (
                      <>
                        <SelectItem value="Development">Development</SelectItem>
                        <SelectItem value="Design">Design</SelectItem>
                        <SelectItem value="Consulting">Consulting</SelectItem>
                        <SelectItem value="Content">Content</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="Software">Software</SelectItem>
                        <SelectItem value="Hardware">Hardware</SelectItem>
                        <SelectItem value="Office">Office</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Services">Services</SelectItem>
                        <SelectItem value="Subscription">Subscription</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={newTransaction.status}
                  onValueChange={value => setNewTransaction(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {newTransaction.type === 'invoice' ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="project">Project</Label>
                  <Input
                    id="project"
                    placeholder="Project name"
                    value={newTransaction.project || ''}
                    onChange={e => setNewTransaction(prev => ({ ...prev, project: e.target.value }))}
                  />
                </div>
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="client">Client</Label>
                  <Input
                    id="client"
                    placeholder="Client name"
                    value={newTransaction.client || ''}
                    onChange={e => setNewTransaction(prev => ({ ...prev, client: e.target.value }))}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="Expense description"
                  value={newTransaction.description || ''}
                  onChange={e => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            )}
            
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="paymentMethod">Payment Method</Label>
              <Select 
                value={newTransaction.paymentMethod}
                onValueChange={value => setNewTransaction(prev => ({ ...prev, paymentMethod: value }))}
              >
                <SelectTrigger id="paymentMethod">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Credit Card">Credit Card</SelectItem>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  {newTransaction.type === 'invoice' && newTransaction.status === 'pending' && (
                    <SelectItem value="Pending">Pending</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTransaction}>
              Add Transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Export Modal */}
      <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Income Data</DialogTitle>
            <DialogDescription>
              Choose a format to export your financial data
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="pdf"
                name="exportFormat"
                className="rounded border-gray-300 text-blue-600"
                checked={exportFormat === 'pdf'}
                onChange={() => setExportFormat('pdf')}
              />
              <Label htmlFor="pdf" className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                PDF Document
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="csv"
                name="exportFormat"
                className="rounded border-gray-300 text-blue-600"
                checked={exportFormat === 'csv'}
                onChange={() => setExportFormat('csv')}
              />
              <Label htmlFor="csv" className="flex items-center">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                CSV Spreadsheet
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="excel"
                name="exportFormat"
                className="rounded border-gray-300 text-blue-600"
                checked={exportFormat === 'excel'}
                onChange={() => setExportFormat('excel')}
              />
              <Label htmlFor="excel" className="flex items-center">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Excel Spreadsheet
              </Label>
            </div>
            
            <div className="pt-3 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox id="includePending" />
                <Label htmlFor="includePending">Include pending transactions</Label>
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <Checkbox id="includeCharts" defaultChecked />
                <Label htmlFor="includeCharts">Include visual charts</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExportModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport}>
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Filter Modal */}
      <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Filter Transactions</DialogTitle>
            <DialogDescription>
              Apply filters to find specific transactions
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-base">Transaction Type</Label>
                  <div className="flex flex-col space-y-2 ml-1">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="typeInvoice"
                        checked={filters.type.includes('invoice')}
                        onCheckedChange={(checked) => {
                          const newTypes = checked 
                            ? [...filters.type, 'invoice'] 
                            : filters.type.filter(t => t !== 'invoice');
                          handleFilterChange('type', newTypes);
                        }}
                      />
                      <Label htmlFor="typeInvoice" className="font-normal">Invoices (Income)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="typeExpense"
                        checked={filters.type.includes('expense')}
                        onCheckedChange={(checked) => {
                          const newTypes = checked 
                            ? [...filters.type, 'expense'] 
                            : filters.type.filter(t => t !== 'expense');
                          handleFilterChange('type', newTypes);
                        }}
                      />
                      <Label htmlFor="typeExpense" className="font-normal">Expenses</Label>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-base">Status</Label>
                  <div className="flex flex-col space-y-2 ml-1">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="statusPaid"
                        checked={filters.status.includes('paid')}
                        onCheckedChange={(checked) => {
                          const newStatus = checked 
                            ? [...filters.status, 'paid'] 
                            : filters.status.filter(s => s !== 'paid');
                          handleFilterChange('status', newStatus);
                        }}
                      />
                      <Label htmlFor="statusPaid" className="font-normal">Paid</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="statusPending"
                        checked={filters.status.includes('pending')}
                        onCheckedChange={(checked) => {
                          const newStatus = checked 
                            ? [...filters.status, 'pending'] 
                            : filters.status.filter(s => s !== 'pending');
                          handleFilterChange('status', newStatus);
                        }}
                      />
                      <Label htmlFor="statusPending" className="font-normal">Pending</Label>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-base">Amount Range</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col space-y-1">
                      <Label htmlFor="minAmount" className="text-xs font-normal">Min ($)</Label>
                      <Input
                        id="minAmount"
                        type="number"
                        value={filters.minAmount}
                        onChange={e => handleFilterChange('minAmount', Number(e.target.value))}
                      />
                    </div>
                    <div className="flex flex-col space-y-1">
                      <Label htmlFor="maxAmount" className="text-xs font-normal">Max ($)</Label>
                      <Input
                        id="maxAmount"
                        type="number"
                        value={filters.maxAmount}
                        onChange={e => handleFilterChange('maxAmount', Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-base">Date Range</Label>
                  <div className="ml-1">
                    <CalendarDateRangePicker 
                      dateRange={
                        filters.dateRange.from && filters.dateRange.to
                          ? { from: filters.dateRange.from, to: filters.dateRange.to }
                          : undefined
                      }
                      setDateRange={(range) => {
                        if (range?.from) {
                          handleFilterChange('dateRange', {
                            from: range.from,
                            to: range.to || range.from,
                          });
                        } else {
                          handleFilterChange('dateRange', { from: null, to: null });
                        }
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-base">Categories</Label>
                  <div className="flex flex-col space-y-2 ml-1 max-h-[150px] overflow-y-auto">
                    {[...new Set([...transactions.map(t => t.category)])].map(category => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`category${category}`}
                          checked={filters.category.includes(category)}
                          onCheckedChange={(checked) => {
                            const newCategories = checked 
                              ? [...filters.category, category] 
                              : filters.category.filter(c => c !== category);
                            handleFilterChange('category', newCategories);
                          }}
                        />
                        <Label htmlFor={`category${category}`} className="font-normal">{category}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between items-center gap-4 sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setFilters({
                  type: [],
                  category: [],
                  status: [],
                  dateRange: { from: null, to: null },
                  minAmount: 0,
                  maxAmount: 10000,
                  search: ''
                });
              }}
              className="mr-auto"
            >
              Reset Filters
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsFilterModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setIsFilterModalOpen(false)}>
                Apply Filters
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Income;
